import { motion, MotionValue, useTransform } from "motion/react";
import styled, { useTheme } from "styled-components";
import React from "react";
import {
  REFRESH_SPINNER_INITIAL_Y,
  REFRESH_SPINNER_LOADING_Y,
  REFRESH_SPINNER_SIZE,
  SCROLL_VIEW_REFETCH_NEGATIVE_Y_POSITION,
  SCROLL_VIEW_REFETCH_Y_POSITION,
} from "common/constants";

interface RefreshSpinnerProps {
  scrollY: MotionValue<number>;
  showLoading?: boolean;
  allowNegativeScroll?: boolean;
  hideAnimation?: boolean;
}

const RefreshSpinner = ({
  scrollY,
  showLoading,
  allowNegativeScroll,
  hideAnimation,
}: RefreshSpinnerProps) => {
  const theme = useTheme();

  const inputRange = allowNegativeScroll ? [0, -100] : [0, 155];
  const y = useTransform(
    scrollY,
    inputRange,
    allowNegativeScroll
      ? [REFRESH_SPINNER_INITIAL_Y, 35]
      : [REFRESH_SPINNER_INITIAL_Y, 90]
  );
  const rotate = useTransform(scrollY, inputRange, [0, 360], { clamp: false });

  const refetchYPosition = allowNegativeScroll
    ? SCROLL_VIEW_REFETCH_NEGATIVE_Y_POSITION
    : SCROLL_VIEW_REFETCH_Y_POSITION;
  const backgroundColor = useTransform(
    scrollY,
    [0, refetchYPosition],
    [theme.colors.lightestGrey, theme.colors.primaryBlue]
  );
  const background = showLoading ? theme.colors.primaryBlue : backgroundColor;

  return (
    <SpinnerBg
      style={{
        y: hideAnimation && showLoading ? REFRESH_SPINNER_LOADING_Y : y,
      }}
    >
      <Spinner $spin={showLoading} style={{ rotate }}>
        <SpinDot style={{ background, top: 0, left: 0 }} />
        <SpinDot style={{ background, opacity: 0.75, top: 0, right: 0 }} />
        <SpinDot style={{ background, opacity: 0.5, bottom: 0, right: 0 }} />
        <SpinDot style={{ background, opacity: 0.25, bottom: 0, left: 0 }} />
      </Spinner>
    </SpinnerBg>
  );
};

export default RefreshSpinner;

const SpinnerBg = styled(motion.div)`
  width: ${REFRESH_SPINNER_SIZE}px;
  height: ${REFRESH_SPINNER_SIZE}px;
  border-radius: 50%;
  box-shadow: 0 6px 24px 0 #2e3d5c1f, 0 2px 2px 0 #0000001a;
  background: ${(p) => p.theme.colors.refreshSpinnerBgColor};
  justify-content: center;
  align-self: center;
  display: flex;
  align-items: center;
  z-index: 2;
  position: absolute;
  left: calc(50% - ${REFRESH_SPINNER_SIZE / 2}px);
`;
const Spinner = styled(motion.div)<{ $spin?: boolean }>`
  width: 20px;
  height: 20px;
  position: relative;
  ${(p) =>
    p.$spin &&
    `
  animation: spin 1s linear infinite;
  `};

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;
const SpinDot = styled(motion.div)`
  position: absolute;
  display: block;
  width: 9px;
  height: 9px;
  border-radius: 100%;
  transform: scale(0.75);
  transform-origin: 50% 50%;
`;
